'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { faqItems } from './skill-data'

// FAQ 的展开收起属于局部交互，只把这段留在 client，避免整页被状态拖成客户端渲染。
export function SkillFAQSection() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)

  return (
    <section className="mx-auto mt-20 w-full max-w-[1192px] px-4 md:mt-24 md:px-6 xl:mt-[128px] xl:px-0">
      <h2 className="text-[32px] font-semibold leading-[1.3] text-[#353745] md:text-[40px] xl:text-[48px] xl:leading-[64px]">常见问题</h2>
      <div className="mt-10 space-y-4 md:mt-12 xl:mt-16 xl:space-y-6">
        {faqItems.map(item => (
          <SkillFAQItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            isHovered={hoveredItem === item.question}
            onHover={() => setHoveredItem(item.question)}
            onLeave={() => setHoveredItem(null)}
          />
        ))}
      </div>
    </section>
  )
}

function SkillFAQItem({
  question,
  answer,
  isHovered,
  onHover,
  onLeave
}: {
  question: string
  answer: string
  isHovered: boolean
  onHover: () => void
  onLeave: () => void
}) {
  return (
    <div className="shadow-input min-h-[68px] w-full overflow-hidden rounded-[12px] bg-[#F6F7F9]" onMouseEnter={onHover} onMouseLeave={onLeave}>
      <div className="flex min-h-[68px] items-center justify-between gap-4 px-4 py-4 md:px-5">
        <div className="w-full pr-4">
          <h3 className="text-[15px] font-semibold leading-[1.5] text-black md:text-[16px] md:leading-[20px]">{question}</h3>
        </div>
        <div className="relative h-4 w-4 shrink-0">
          <svg
            className={cn('absolute inset-0 h-4 w-4 transform stroke-[1.5] text-black transition-all duration-200', isHovered && 'rotate-180')}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path d="M4.44897 10.0716L8 5.92871L11.551 10.0716" stroke="#8A8FA8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      <AnimatePresence mode="wait">
        {isHovered && (
          <motion.div
            initial={{ height: 0, padding: 0 }}
            animate={{ height: 'auto', padding: '1rem 1.25rem' }}
            exit={{ height: 0, padding: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative overflow-hidden text-[15px] font-normal leading-[1.7] text-[#555974] md:text-[16px] md:leading-[26px]"
          >
            <motion.div className="absolute left-1/2 top-0 -translate-x-1/2 border-t border-white/70" initial={{ width: '0' }} animate={{ width: '100%' }} exit={{ width: '0' }} />
            {answer.split('').map((character, index) => (
              <motion.span
                initial={{ opacity: 0, filter: 'blur(5px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: 0.2,
                  ease: 'easeOut',
                  delay: index * 0.005
                }}
                key={`${question}-${index}`}
              >
                {character}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
